export type RecorderOptions = {
  sampleRate?: number;
  onAudioData: (buffer: ArrayBuffer) => void;
};

export class Recorder {
  private audioContext: AudioContext | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private isInitialized = false;
  private readonly sampleRate: number;
  private readonly onAudioData: (buffer: ArrayBuffer) => void;

  constructor({ sampleRate = 24000, onAudioData }: RecorderOptions) {
    this.sampleRate = sampleRate;
    this.onAudioData = onAudioData;
  }

  async start(stream: MediaStream) {
    if (this.isInitialized) {
      return;
    }

    // Use the hardware's native sample rate to keep the browser's AEC
    // pipeline intact, then let the AudioWorklet down-sample to the
    // target rate.  Creating a context at a non-native rate can bypass
    // echo cancellation on some browsers/drivers.
    this.audioContext = new AudioContext();
    await this.audioContext.audioWorklet.addModule('/audio-processor-worklet.js');

    this.mediaStreamSource = this.audioContext.createMediaStreamSource(stream);

    const nativeRate = this.audioContext.sampleRate;
    const needsResample = nativeRate !== this.sampleRate;

    this.workletNode = new AudioWorkletNode(this.audioContext, 'audio-processor-worklet');
    if (needsResample) {
      this.workletNode.port.postMessage({
        type: 'configure',
        targetSampleRate: this.sampleRate,
        inputSampleRate: nativeRate
      });
    }
    this.workletNode.port.onmessage = event => {
      if (event.data) {
        this.onAudioData(event.data);
      }
    };

    this.mediaStreamSource.connect(this.workletNode);

    // Connect to destination with zero gain to keep the graph alive without playback
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = 0;
    this.workletNode.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    this.isInitialized = true;
  }

  async dispose() {
    try {
      this.workletNode?.port.postMessage({ type: 'stop' });
    } catch {
      // ignore
    }

    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.mediaStreamSource) {
      this.mediaStreamSource.disconnect();
      this.mediaStreamSource = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    this.isInitialized = false;
  }

  isReady() {
    return this.isInitialized;
  }
}

